import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { formatMoney as formatMoneyUtil, parseMoney as parseMoneyUtil } from '../utils/currency';
import { getCurrentUser, updateUserProfile } from '../services/authService';

interface UserPreferences {
  currency: string;
  thousandSeparator: string;
  language: string;
}

interface UserPreferencesContextType {
  preferences: UserPreferences;
  updatePreferences: (currency: string, separator: string, language: string) => Promise<void>;
  formatMoney: (amount: number) => string;
  parseMoney: (input: string) => number;
  loading: boolean;
  clearPreferences: () => void;
}

const defaultPreferences: UserPreferences = {
  currency: 'IDR',
  thousandSeparator: '.',
  language: 'id',
};

const UserPreferencesContext = createContext<UserPreferencesContextType | undefined>(undefined);

export const useUserPreferences = () => {
  const context = useContext(UserPreferencesContext);
  if (!context) {
    throw new Error('useUserPreferences must be used within a UserPreferencesProvider');
  }
  return context;
};

// Helper to get user-specific localStorage key
const getStorageKey = (userId: string) => `user_preferences_${userId}`;

interface UserPreferencesProviderProps {
  children: ReactNode;
}

export const UserPreferencesProvider: React.FC<UserPreferencesProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<UserPreferences>(defaultPreferences);
  const [loading, setLoading] = useState(true);

  // Clear preferences (called on logout)
  const clearPreferences = useCallback(() => {
    setPreferences(defaultPreferences);
  }, []);

  // Load preferences from backend when user changes
  useEffect(() => {
    const loadPreferences = async () => {
      if (!user) {
        // User logged out - reset to defaults
        setPreferences(defaultPreferences);
        setLoading(false);
        
        // Reset language to default on logout
        import('../i18n').then(i18n => {
          i18n.default.changeLanguage(defaultPreferences.language);
        });
        
        // Clean up old global key (migration from old implementation)
        localStorage.removeItem('user_preferences');
        return;
      }

      setLoading(true);
      const storageKey = getStorageKey(user.id);

      try {
        // First, try to get from user-specific localStorage (fast)
        const cached = localStorage.getItem(storageKey);
        if (cached) {
          const cachedPrefs = JSON.parse(cached);
          setPreferences(cachedPrefs);
        }

        // Then fetch from backend to ensure we have latest
        const userProfile = await getCurrentUser();
        
        const backendPrefs: UserPreferences = {
          currency: userProfile.preferred_currency || 'IDR',
          thousandSeparator: userProfile.thousand_separator || '.',
          language: userProfile.language || 'id',
        };

        // Update state and cache
        setPreferences(backendPrefs);
        localStorage.setItem(storageKey, JSON.stringify(backendPrefs));

        // Also update i18n language
        import('../i18n').then(i18n => {
          i18n.default.changeLanguage(backendPrefs.language);
        });

      } catch (error) {
        console.error('Failed to load preferences from backend:', error);
        // Fall back to cached or defaults
        const cached = localStorage.getItem(storageKey);
        if (cached) {
          setPreferences(JSON.parse(cached));
        }
      } finally {
        setLoading(false);
      }
    };

    loadPreferences();
  }, [user]);

  const updatePreferences = async (currency: string, separator: string, language: string) => {
    if (!user) {
      console.error('Cannot update preferences: no user logged in');
      return;
    }

    const storageKey = getStorageKey(user.id);
    const newPrefs = { currency, thousandSeparator: separator, language };

    // Optimistic update
    setPreferences(newPrefs);
    localStorage.setItem(storageKey, JSON.stringify(newPrefs));

    try {
      // Update backend
      await updateUserProfile({
        preferred_currency: currency,
        thousand_separator: separator,
        language: language,
      });
    } catch (error) {
      console.error('Failed to update preferences in backend:', error);
      // Don't revert - the local update still works for this session
      // Backend will be updated on next successful save
    }
  };

  const formatMoney = (amount: number) => {
    return formatMoneyUtil(amount, preferences.currency, preferences.thousandSeparator);
  };

  const parseMoney = (input: string) => {
    return parseMoneyUtil(input, preferences.thousandSeparator);
  };

  const value = {
    preferences,
    updatePreferences,
    formatMoney,
    parseMoney,
    loading,
    clearPreferences,
  };

  return <UserPreferencesContext.Provider value={value}>{children}</UserPreferencesContext.Provider>;
};
